<template>
  <div class="selo-ia-validacao">
    <header>
      <div>
        <small>ANÁLISE DE EVIDÊNCIAS IA · VALIDAÇÃO</small>
        <h1>Validação das análises</h1>
        <p>
          Confira o critério, os arquivos e a avaliação oficial antes de
          sincronizar.
        </p>
      </div>
      <div class="actions">
        <button @click="load" :disabled="busy">Atualizar</button
        ><button
          class="primary"
          @click="syncReady"
          :disabled="busy || !ready.length"
        >
          Sincronizar validados ({{ ready.length }})
        </button>
      </div>
    </header>
    <div v-if="error" class="message error" role="alert">{{ error }}</div>
    <div v-if="notice" class="message info" role="status">{{ notice }}</div>
    <div v-if="!enabled" class="message info">
      {{
        loading
          ? "Carregando análises…"
          : "Nenhuma rodada está disponibilizada para validação neste ambiente."
      }}
    </div>
    <template v-if="enabled">
      <div class="tabs">
        <button
          :class="{ active: !q3 }"
          @click="
            q3 = false;
            load();
          "
        >
          Demais critérios</button
        ><button
          :class="{ active: q3 }"
          @click="
            q3 = true;
            load();
          "
        >
          Critério 5 · Capacitação
        </button>
      </div>
      <div class="filters">
        <label
          >Sala<select v-model.number="room" @change="load">
            <option v-for="r in rooms" :key="r.id" :value="r.id">
              {{ r.nome }}
            </option>
          </select></label
        ><label
          >Buscar critério, arquivo ou parecer<input
            v-model="search"
            placeholder="Digite para filtrar" /></label
        ><label
          >Situação<select v-model="stateFilter">
            <option value="">Todas</option>
            <option value="PRONTO">Prontos</option>
            <option value="REABERTO">Reabertos</option>
            <option value="SINCRONIZADO">Sincronizados</option>
            <option value="NOVA_ANALISE">Nova análise necessária</option>
            <option value="DECISAO_OFICIAL_ALTERADA">
              Avaliação oficial alterada
            </option>
          </select></label
        >
      </div>
      <div v-if="loading" class="message info loading-state" role="status" aria-live="polite">
        <span class="loading-spinner" aria-hidden="true"></span>
        Carregando {{ q3 ? 'critérios de capacitação' : 'demais critérios' }} da Sala… Aguarde.
      </div>
      <div v-if="!loading" class="metrics">
        <span
          ><strong>{{ rows.length }}</strong> critérios</span
        ><span
          ><strong>{{
            rows.filter((r) => r.estado === "SINCRONIZADO").length
          }}</strong>
          sincronizados</span
        ><span
          ><strong>{{ ready.length }}</strong> prontos nesta seleção</span
        ><span
          ><strong>{{ rows.filter((r) => r.arquivosAlterados).length }}</strong>
          precisam de nova análise</span
        >
      </div>
      <div v-if="!loading" class="grid-head">
        <span></span><span>Sala</span><span>Critério / quesito</span
        ><span>Resultado da IA</span><span>Pontos</span
        ><span>Concordância humana</span>
      </div>
      <section
        v-for="row in filtered"
        :key="row.id + '-' + row.versao + '-' + radioEpoch"
        class="criterion"
      >
        <div class="mainrow">
          <button
            class="toggle"
            @click="$set(expanded, row.id, !expanded[row.id])"
            :aria-label="'Expandir critério ' + row.analise.code"
            :aria-expanded="!!expanded[row.id]"
          >
            {{ expanded[row.id] ? "⌄" : "›" }}
          </button>
          <div>
            <strong>{{ shortRoom(row.analise.room) }}</strong
            ><small>{{ row.analise.files.length }} arquivo(s)</small>
          </div>
          <div class="question">
            <b>{{ row.analise.code }}</b> · {{ row.analise.title }}
          </div>
          <div>
            <span
              class="pill"
              :class="tone(row.analise.points, row.analise.max)"
              >{{ row.analise.result }}</span
            >
          </div>
          <div class="points">
            {{ num(row.analise.points)
            }}<small>/ {{ num(row.analise.max) }}</small>
          </div>
          <div class="flags">
            <button v-if="q3 && row.revisao && row.revisao.checklistRefeito"
              :disabled="busy || row.arquivosAlterados || !pendingFiles(row).length"
              @click="confirmPending(row)">Confirmar arquivos pendentes</button>
            <label v-else
              ><input
                type="radio"
                :name="'p' + row.id"
                :checked="row.revisao && row.revisao.flag === 'CONCORDO'"
                :disabled="busy || row.arquivosAlterados"
                @change="mark(row, null, 'CONCORDO')"
              />Concordo com a IA</label
            ><label
              ><input
                type="radio"
                :name="'p' + row.id"
                :checked="row.revisao && row.revisao.flag === 'DISCORDO'"
                :disabled="busy || row.arquivosAlterados"
                @change="mark(row, null, 'DISCORDO')"
              />Discordo da IA</label
            >
          </div>
        </div>
        <div class="criterion-meta">
          <span
            ><b>Faixa:</b> {{ row.analise.band }}
            <template v-if="q3">
              · <b>Atendentes conformes:</b>
              {{ row.analise.validItems }}</template
            ><template v-else>
              · <b>Itens válidos do conjunto:</b>
              {{ row.analise.validItems }}</template
            ></span
          ><span class="pill" :class="stateTone(row.estado)">{{
            stateLabel(row.estado)
          }}</span
          ><button
            v-if="row.podeSincronizar"
            @click="sync(row)"
            :disabled="busy"
          >
            Sincronizar este critério</button
          ><button
            v-if="row.revisao"
            @click="edit(row, null, 'NOVA')"
            :disabled="busy || row.arquivosAlterados"
          >
            Rever validação</button
          ><button @click="showHistory(row)">Histórico</button>
        </div>
        <div
          v-if="row.mensagem"
          class="message"
          :class="
            row.arquivosAlterados || row.estado === 'DECISAO_OFICIAL_ALTERADA'
              ? 'warning'
              : 'info'
          "
        >
          {{ row.mensagem
          }}<button
            v-if="row.estado === 'DECISAO_OFICIAL_ALTERADA'"
            @click="compare(row, null, 'CONCORDO')"
          >
            Reabrir comparação
          </button>
        </div>
        <div v-if="expanded[row.id]" class="detail">
          <div class="comparison">
            <h3>Parecer consolidado do critério</h3>
            <p>{{ row.analise.remark }}</p>
          </div>
          <div
            v-if="row.mudancaContexto === 'ARQUIVOS'"
            class="message warning"
          >
            <b
              >Arquivos atuais: {{ row.arquivosAtuais.length }} · Arquivos
              considerados pela IA: {{ row.analise.files.length }}</b
            >
            <ul>
              <li
                v-for="f in row.arquivosAtuais.filter(
                  (f) => !row.analise.files.some((a) => a.id === f.id)
                )"
                :key="f.id"
              >
                Ainda não analisado: {{ f.name }}
              </li>
            </ul>
            <p>
              A sincronização deste critério está bloqueada até uma nova
              avaliação do conjunto completo.
            </p>
          </div>
          <div v-if="row.avaliacaoOficialExistente" class="comparison">
            <h3>{{ row.oficial.criterion.action ? 'IA × avaliação humana existente' : 'Avaliações humanas existentes nos arquivos' }}</h3>
            <div class="comparison-grid">
              <div>
                <b>Sugestão da IA</b>
                <p>
                  {{ row.analise.result }} · {{ num(row.analise.points) }} /
                  {{ num(row.analise.max) }} pontos
                </p>
              </div>
              <div v-if="row.oficial.criterion.action">
                <b>Avaliação humana oficial</b>
                <p>
                  {{
                    row.oficial.criterion.action ||
                    "Critério ainda não concluído"
                  }}
                  ·
                  {{
                    row.oficial.criterion.points == null
                      ? "Sem pontuação definida"
                      : num(row.oficial.criterion.points) + " pontos"
                  }}
                </p>
                <small
                  >{{
                    row.oficial.criterion.user ||
                    "Autoria não identificada no registro"
                  }}
                  · {{ date(row.oficial.criterion.at) }}</small
                >
                <p v-if="row.oficial.criterion.comment">
                  {{ row.oficial.criterion.comment }}
                </p>
              </div>
            </div>
            <p v-if="row.avaliacoesCoincidem">
              As decisões coincidem. Você pode confirmá-las ou fazer outra
              avaliação.
            </p>
            <p v-else>
              Não haverá substituição automática: escolha qual avaliação deve
              prevalecer.
            </p>
            <button
              @click="compare(row, null, 'CONCORDO')"
              :disabled="busy || row.arquivosAlterados"
            >
              Comparar e escolher avaliação
            </button>
          </div>
          <div v-if="row.revisao" class="human">
            <b>Validação humana · versão {{ row.versao }}</b>
            <p>
              {{ row.revisao.action }} · {{ num(row.revisao.points) }} /
              {{ num(row.analise.max) }} pontos
            </p>
            <p v-if="row.revisao.justification">
              {{ row.revisao.justification }}
            </p>
            <small v-if="row.sincronizadaEm"
              >Última sincronização: {{ date(row.sincronizadaEm) }}. Revisões
              posteriores precisam de nova sincronização.</small
            >
          </div>
          <div v-if="q3 && row.revisao && pendingFiles(row).length" class="message warning" role="status">
            <b>Revisão salva. Falta confirmar {{ pendingFiles(row).length }} arquivo(s):</b>
            <ul><li v-for="f in pendingFiles(row)" :key="f.id">{{ f.name }}</li></ul>
            <p>Confira cada arquivo e marque Concordo ou Discordo. As revisões já salvas serão preservadas.</p>
            <button @click="confirmPending(row)" :disabled="busy || row.arquivosAlterados">Concordar com a IA somente nos arquivos pendentes</button>
          </div>
          <details v-if="q3 && humanChecklist(row)" class="checklist human" :open="true">
            <summary>{{ row.estado === 'SINCRONIZADO' ? 'Checklist humano sincronizado na análise oficial' : 'Checklist humano vigente / revisão salva' }}</summary>
            <p><b>{{ row.revisao && row.estado !== 'DECISAO_OFICIAL_ALTERADA' ? num(row.revisao.points) : num(row.oficial.criterion.points) }} / {{ num(row.analise.max) }} pontos.</b>
              {{ row.estado === 'SINCRONIZADO' ? 'Esta é a decisão publicada.' : 'Confira a situação da sincronização antes de encerrar.' }}</p>
            <p>Quantidade e nomes: <b>{{ boolText(humanChecklist(row).equipeConfere) }}</b> · Horas de 2026: <b>{{ boolText(humanChecklist(row).ano2026Confere) }}</b></p>
            <details v-for="p in humanChecklist(row).atendentes" :key="p.chave" class="person" open>
              <summary>{{ p.nome }} · <b>{{ num(p.horasValidas) }}h válidas</b>
                <small>{{ personReviewed(row, p.chave) ? 'Revisado pelo humano' : 'Dados preservados da versão anterior' }}</small></summary>
              <div v-for="item in checks" :key="item.key" class="checkline"><span>{{ item.label }}</span><b>{{ boolText(p.itens[item.key]) }}</b></div>
              <p>{{ p.observacoes }}</p>
            </details>
            <div v-for="d in humanChecklist(row).declaracoes" :key="d.arquivoId" class="person">
              <b>{{ fileName(row, d.arquivoId) }} · {{ d.resultado }}</b>
              <p>Tempestiva: {{ boolText(d.tempestiva) }} · Assinada: {{ boolText(d.assinada) }} · Dados completos: {{ boolText(d.modeloAdequado) }}</p><p>{{ d.justificativa }}</p>
            </div>
            <p>O checklist original da IA abaixo é apenas referência e não substitui esta revisão. Para alterar novamente, use <b>Rever validação</b>.</p>
          </details>
          <details v-if="q3 && row.analise.c5" class="checklist" :open="!humanChecklist(row)">
            <summary>Checklist original da IA — preservado para comparação</summary>
            <div class="team">
              <div>
                <b>Equipe considerada pela IA</b>
                <p>{{ row.analise.c5.quantidadeEquipeAtual }} atendente(s)</p>
                <p v-for="p in row.analise.c5.atendentes" :key="p.chave">
                  {{ p.nome }}
                </p>
              </div>
              <div>
                <b>Conferência geral</b>
                <p>
                  Quantidade e nomes:
                  <b>{{ boolText(row.analise.c5.equipeConfere) }}</b>
                </p>
                <p>
                  Horas válidas de 2026:
                  <b>{{ boolText(row.analise.c5.ano2026Confere) }}</b>
                </p>
                <p v-if="typeof row.analise.c5.arquivosConformesRegulamento === 'boolean'">
                  Padrões documentais do regulamento:
                  <b>{{ boolText(row.analise.c5.arquivosConformesRegulamento) }}</b>
                </p>
              </div>
            </div>
            <details v-if="row.oficial.registration" class="person">
              <summary>Conferir quantidade e nomes da inscrição</summary>
              <p>Quantidade: {{ row.oficial.registration.quantity }}</p>
              <p>{{ row.oficial.registration.names }}</p>
              <p>
                Uma declaração de equipe aprovada substitui essa referência. Sem
                declaração válida, prevalecem os dados da inscrição.
              </p>
            </details>
            <details
              v-for="d in row.analise.c5.declaracoes"
              :key="d.arquivoId"
              class="person"
            >
              <summary>
                Declaração · {{ fileName(row, d.arquivoId) }} ·
                {{ d.resultado }}
              </summary>
              <div class="checkline">
                <span>Tempestiva</span><b>{{ boolText(d.tempestiva) }}</b>
              </div>
              <div class="checkline">
                <span>Assinada</span><b>{{ boolText(d.assinada) }}</b>
              </div>
              <div class="checkline">
                <span>Modelo e dados completos</span
                ><b>{{ boolText(d.modeloAdequado) }}</b>
              </div>
              <p>{{ d.justificativa }}</p>
            </details>
            <details
              v-for="(p, index) in row.analise.c5.atendentes"
              :key="p.chave"
              class="person"
              :open="index === 0"
            >
              <summary>
                {{ p.nome }} · {{ num(p.horasValidas) }}h válidas
              </summary>
              <div v-for="item in checks" :key="item.key" class="checkline">
                <span>{{ item.label }}</span
                ><b :class="p.itens[item.key] ? 'text-green' : 'text-red'">{{
                  boolText(p.itens[item.key])
                }}</b>
              </div>
              <p>{{ p.observacoes }}</p>
              <details>
                <summary>Certificados, cursos e páginas</summary>
                <div v-for="(c, n) in courses(row, p)" :key="n" class="course">
                  <b>{{ c.title }}</b
                  ><span
                    >{{ num(c.hours) }}h ·
                    {{ c.valid ? "Computado" : "Não computado" }} ·
                    {{ c.start }} a {{ c.end }}</span
                  ><a
                    :href="c.sourceUrl + '#page=' + c.page"
                    target="_blank"
                    rel="noopener"
                    >{{ c.fileName }} · página {{ c.page }}</a
                  ><small>{{ c.note }}</small>
                </div>
              </details>
            </details>
            <p>{{ row.analise.remark }}</p>
            <button
              v-if="!humanChecklist(row)"
              @click="edit(row, null, 'NOVA')"
              :disabled="busy || row.arquivosAlterados"
            >
              Revisar atendentes ou toda a equipe
            </button>
          </details>
          <h3>Arquivos de evidência</h3>
          <article
            v-for="file in row.analise.files"
            :key="file.id"
            class="evidence"
          >
            <div class="filehead">
              <a :href="file.url" target="_blank" rel="noopener"
                >↗ {{ file.name }}</a
              ><span class="pill" :class="actionTone(file.decision)">{{
                file.decision
              }}</span>
              <div class="flags">
                <label
                  ><input
                    type="radio"
                    :name="'f' + row.id + '-' + file.id"
                    :checked="fileReview(row, file.id).flag === 'CONCORDO'"
                    :disabled="busy || row.arquivosAlterados"
                    @change="mark(row, file.id, 'CONCORDO')"
                  />Concordo com a IA</label
                ><label
                  ><input
                    type="radio"
                    :name="'f' + row.id + '-' + file.id"
                    :checked="fileReview(row, file.id).flag === 'DISCORDO'"
                    :disabled="busy || row.arquivosAlterados"
                    @change="mark(row, file.id, 'DISCORDO')"
                  />Discordo da IA</label
                >
              </div>
            </div>
            <div class="copygrid">
              <div>
                <b>Descrição do encontrado</b>
                <p>{{ file.found }}</p>
              </div>
              <div>
                <b>Parecer técnico</b>
                <p>{{ file.opinion }}</p>
              </div>
            </div>
            <div v-if="officialFile(row, file.id).action" class="official-file">
              <b>Avaliação humana oficial:</b>
              {{ officialFile(row, file.id).action }} ·
              {{
                officialFile(row, file.id).user || "Autoria não identificada"
              }}
              · {{ date(officialFile(row, file.id).at) }}
              <p>{{ officialFile(row, file.id).justification }}</p>
            </div>
            <div
              v-if="fileReview(row, file.id).flag === 'DISCORDO'"
              class="human"
            >
              <b>Ação proposta:</b> {{ fileReview(row, file.id).action }}
              <p>{{ fileReview(row, file.id).justification }}</p>
            </div>
          </article>
        </div>
      </section>
      <p v-if="!loading && !filtered.length" class="message info">
        Nenhum critério nesta seleção.
      </p>
    </template>
    <div
      v-if="dialog"
      class="overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="validation-title"
    >
      <div class="dialog">
        <header>
          <h2 id="validation-title">{{ dialogTitle }}</h2>
          <button aria-label="Fechar" @click="close">×</button>
        </header>
        <div class="dialog-body">
          <div v-if="dialog === 'compare'">
            <p>
              Escolha explicitamente qual avaliação deve prevalecer para
              {{ current.analise.code }} ·
              {{ shortRoom(current.analise.room) }}.
            </p>
            <div class="comparison-grid">
              <div>
                <h3>IA</h3>
                <p>{{ current.analise.result }}</p>
                <p>
                  {{ num(current.analise.points) }} /
                  {{ num(current.analise.max) }} pontos
                </p>
              </div>
              <div>
                <h3>Humana existente</h3>
                <p v-if="!current.oficial.criterion.action">Não foi realizada avaliação humana</p>
                <template v-else><p>
                  {{
                    current.oficial.criterion.action || "Ainda não concluída"
                  }}
                </p>
                <p>
                  {{
                    current.oficial.criterion.points == null
                      ? "Sem pontuação"
                      : num(current.oficial.criterion.points) + " pontos"
                  }}
                </p>
                <small
                  >{{ current.oficial.criterion.user }} ·
                  {{ date(current.oficial.criterion.at) }}</small
                >
                </template>
              </div>
            </div>
            <div
              v-for="f in current.analise.files"
              :key="f.id"
              class="comparison-file"
            >
              <b>{{ f.name }}</b
              ><span>IA: {{ f.decision }}</span
              ><span
                >Humana:
                {{
                  officialFile(current, f.id).action || "Não analisado"
                }}</span
              >
              <p>{{ officialFile(current, f.id).justification }}</p>
            </div>
            <div class="choice-buttons">
              <button class="primary" @click="choose('IA')">
                Aceitar avaliação da IA</button
              ><button
                @click="choose('OFICIAL')"
                :disabled="!current.oficial.complete"
              >
                Aceitar avaliação humana existente</button
              ><button @click="choose('NOVA')">
                Refutar as duas e fazer nova avaliação
              </button>
            </div>
            <div class="message warning">
              A decisão anterior será preservada no histórico. Se os arquivos ou
              a avaliação oficial mudarem antes da sincronização, será
              necessária nova conferência.
            </div>
          </div>
          <div v-if="dialog === 'edit'">
            <div class="message info">
              {{
                targetFile
                  ? "A discordância deste arquivo marca também o critério como discordante."
                  : "Revise o resultado consolidado do critério."
              }}
              <template v-if="current.analise.questionId === 3"
                >Selecione os atendentes que precisam de revisão. Os demais dados serão preservados.</template
              >
            </div>
            <p>
              <b>{{ current.analise.code }} · {{ current.analise.title }}</b>
            </p>
            <p v-if="targetFile">
              Arquivo: {{ fileName(current, targetFile) }}
            </p>
            <label class="field"
              >Justificativa da discordância *<textarea
                v-model="form.justification"
                rows="3"
                placeholder="Informe o que deve mudar e os fatos que justificam a alteração."
              ></textarea>
            </label>
            <label v-if="targetFile" class="field"
              >Ação correta para este arquivo *<select
                v-model="form.fileAction"
              >
                <option value="">Selecione</option>
                <option v-for="a in actionsList" :key="a">{{ a }}</option>
              </select></label
            >
            <div v-else class="file-decisions">
              <h3>Nova avaliação dos arquivos</h3>
              <label v-for="f in form.files" :key="f.id" class="field"
                >{{ fileName(current, f.id)
                }}<select v-model="f.action">
                  <option value="">Selecione a ação *</option>
                  <option v-for="a in actionsList" :key="a">{{ a }}</option>
                </select></label
              >
            </div>
            <label
              v-if="
                current.analise.questionId !== 3 && current.analise.bands.length
              "
              class="field"
              >Nova faixa do critério *<select v-model.number="form.bandId">
                <option :value="null">Selecione</option>
                <option
                  v-for="b in current.analise.bands"
                  :key="b.id"
                  :value="b.id"
                >
                  {{ b.label }} — {{ num(b.points) }} ponto(s)
                </option>
              </select></label
            >
            <label
              v-if="
                current.analise.questionId !== 3 &&
                !current.analise.bands.length
              "
              class="field"
              >Nova ação do critério (tudo ou nada) *<select
                v-model="form.action"
              >
                <option value="">Selecione</option>
                <option value="APROVAR">APROVAR</option>
                <option value="REJEITAR">REJEITAR</option>
              </select></label
            >
            <section v-if="current.analise.questionId === 3" class="checklist">
              <h3>Revisão por atendente</h3>
              <p>As respostas foram carregadas da versão vigente. Confira e altere somente o necessário.</p>
              <div ref="c5Selection" tabindex="-1" class="message info" :class="{ 'field-error': editIssue === 'c5Selection' }">
                <b>Quem precisa de revisão?</b>
                <small v-if="editIssue === 'c5Selection'" class="field-error-text">{{ dialogError }}</small>
                <label v-for="p in form.c5.atendentes" :key="'scope-' + p.chave" class="checkbox">
                  <input type="checkbox" v-model="form.selectedPeople" :value="p.chave" :disabled="form.wholeTeam" />
                  {{ p.nome }} · {{ num(p.horasValidas) }}h
                </label>
                <p v-if="targetFile">A seleção inicial usa os vínculos com este arquivo. Em um PDF coletivo, selecione somente os atendentes afetados.</p>
                <button @click="reviewWholeTeam">Revisar toda a equipe</button>
              </div>
              <label class="checkbox"><input type="checkbox" v-model="form.reviewGlobal" :disabled="form.wholeTeam" />
                Revisar conferências gerais e declarações da equipe</label>
              <p v-if="!form.reviewGlobal">Conferências gerais, declarações e arquivos desconsiderados serão preservados.</p>
              <div v-if="current.oficial.registration" class="message info">
                <b
                  >Equipe informada na inscrição:
                  {{ current.oficial.registration.quantity }} atendente(s)</b
                >
                <p>{{ current.oficial.registration.names }}</p>
                <p>
                  Se aprovar uma declaração válida de alteração, confira a
                  equipe da declaração. Caso contrário, use a inscrição para
                  revisar os integrantes abaixo.
                </p>
              </div>
              <label v-for="c in generalChecks" :key="c.key" class="field"
                >{{ c.label }} *<select v-model="form.c5[c.key]" :disabled="!form.reviewGlobal">
                  <option :value="null">Conferir</option>
                  <option :value="true">Sim</option>
                  <option :value="false">Não</option>
                </select></label
              >
              <details
                v-for="d in form.c5.declaracoes"
                :key="d.arquivoId"
                class="person"
                open
              >
                <summary>
                  Declaração · {{ fileName(current, d.arquivoId) }}
                </summary>
                <label v-for="c in declarationChecks" :key="c.key" class="field"
                  >{{ c.label }} *<select v-model="d[c.key]" :disabled="!form.reviewGlobal">
                    <option :value="null">Conferir</option>
                    <option :value="true">Cumprido</option>
                    <option :value="false">Não cumprido</option>
                  </select></label
                ><label class="field"
                  >Decisão da declaração *<select v-model="d.resultado" :disabled="!form.reviewGlobal">
                    <option value="">Selecione</option>
                    <option value="aprovar">Aprovar</option>
                    <option value="rejeitar">Rejeitar</option>
                  </select></label
                ><label v-if="d.resultado === 'rejeitar'" class="field"
                  >Justificativa da declaração *<textarea
                    v-model="d.justificativa"
                    rows="2"
                    :disabled="!form.reviewGlobal"
                  ></textarea>
                </label>
              </details>
              <details
                v-for="(p, index) in reviewPeople"
                :key="p.chave"
                class="person"
                :open="index === 0"
              >
                <summary>
                  {{ p.nome || "Novo atendente" }} · revisão selecionada
                </summary>
                <label class="field"
                  >Nome do atendente *<input v-model="p.nome" /></label
                ><label class="field"
                  >Arquivos deste atendente<select
                    v-model="p.arquivoIds"
                    multiple
                  >
                    <option
                      v-for="f in certificateFiles"
                      :key="f.id"
                      :value="f.id"
                    >
                      {{ f.name }}
                    </option>
                  </select></label
                ><label class="checkbox"
                  ><input type="checkbox" v-model="p.semCertificado" />Nenhum
                  certificado apresentado para este atendente</label
                >
                <div v-for="c in checks" :key="c.key" class="checkline">
                  <span
                    >{{ c.label
                    }}<small
                      >IA:
                      {{
                        boolText(
                          aiPerson(p.chave).itens &&
                            aiPerson(p.chave).itens[c.key]
                        )
                      }}</small
                    ></span
                  ><select v-model="p.itens[c.key]">
                    <option :value="null">Conferir *</option>
                    <option :value="true">Cumprido</option>
                    <option :value="false">Não cumprido</option>
                  </select>
                </div>
                <label class="field"
                  >Horas válidas confirmadas pelo humano *<input
                    v-model.number="p.horasValidas"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="Informe sem arredondar para hora inteira" /></label
                ><label class="field"
                  >Fundamentos deste atendente<textarea
                    v-model="p.observacoes"
                    rows="2"
                  ></textarea></label
                ><button v-if="form.wholeTeam" @click="removePerson(p.chave)">
                  Remover atendente desta revisão
                </button>
              </details>
              <button v-if="form.wholeTeam" @click="addPerson">Adicionar atendente à revisão</button>
              <details v-if="form.reviewGlobal" class="person">
                <summary>
                  Arquivos desconsiderados para a contagem da equipe
                </summary>
                <label
                  v-for="f in certificateFiles"
                  :key="f.id"
                  class="field checkbox"
                  ><input
                    type="checkbox"
                    v-model="form.ignoredIds"
                    :value="f.id"
                  />{{ f.name }}</label
                >
                <p>
                  Use apenas para documentos que não devem ser atribuídos à
                  equipe vigente. A justificativa é obrigatória e não se criam
                  horas ou vínculos por presunção.
                </p>
              </details>
              <div ref="c5Preview" tabindex="-1" class="message info" :class="{ 'field-error': editIssue === 'c5Preview' }" aria-live="polite">
                <small v-if="editIssue === 'c5Preview'" class="field-error-text">{{ dialogError }}</small>
                {{
                  calculated.complete
                    ? "Prévia: " +
                      num(calculated.points) +
                      " / 7 pontos. Menor carga: " +
                      num(calculated.min) +
                      "h."
                    : "Complete os dados pendentes para calcular a faixa da equipe. As respostas existentes foram preservadas."
                }}
                <p>A edição é por atendente; a pontuação considera toda a equipe, incluindo os dados preservados dos demais.</p>
                <ul v-if="calculated.complete"><li v-for="(reason, index) in teamScoreReasons" :key="index">{{ reason }}</li></ul>
              </div>
              <p>Antes de salvar, selecione a faixa calculada e marque a confirmação da conferência abaixo.</p>
              <label class="field" :class="{ 'field-error': editIssue === 'c5Points' }"
                >Confirmar nova faixa do critério 5 *<select
                  ref="c5Points"
                  v-model.number="form.points"
                  :aria-invalid="editIssue === 'c5Points'"
                  aria-describedby="c5-points-error"
                >
                  <option :value="null">Selecione após a conferência</option>
                  <option v-if="calculated.complete" :value="calculated.points">
                    {{ num(calculated.points) }} ponto(s), conforme checklist
                  </option>
                </select><small id="c5-points-error" v-if="editIssue === 'c5Points'" class="field-error-text">{{ dialogError }}</small></label
              >
              <label class="checkbox" :class="{ 'field-error': editIssue === 'c5Confirmation' }"><input ref="c5Confirmation" type="checkbox" v-model="form.scopeConfirmed" :aria-invalid="editIssue === 'c5Confirmation'" aria-describedby="c5-confirmation-error" />
                Conferi os atendentes selecionados e, quando habilitados, os dados gerais. Os demais dados devem ser mantidos.</label>
              <small id="c5-confirmation-error" v-if="editIssue === 'c5Confirmation'" class="field-error-text">{{ dialogError }}</small>
            </section>
          </div>
          <div v-if="dialog === 'history'">
            <p>
              A análise original da IA permanece preservada. Cada revisão,
              reabertura e sincronização registra usuário, data e conteúdo
              anterior.
            </p>
            <details v-for="h in historyRows" :key="h.id" class="person">
              <summary>
                {{ h.acao }} · versão {{ h.versao }} ·
                {{ h.usuario || "Usuário registrado" }} ·
                {{ date(h.criado_em) }}
              </summary>
              <p v-for="(line, n) in historyLines(h.dados_json)" :key="n">{{ line }}</p>
            </details>
          </div>
          <div v-if="dialogError" class="message error" role="alert">
            {{ dialogError }}
          </div>
        </div>
        <footer>
          <small
            >As decisões oficiais só serão movimentadas pelo botão de
            sincronização.</small
          >
          <div>
            <button @click="close" :disabled="busy">
              {{ dialog === "history" ? "Fechar" : "Cancelar" }}</button
            ><button
              v-if="dialog === 'edit'"
              class="primary"
              @click="saveEdit"
              :disabled="busy"
            >
              {{
                current.analise.questionId === 3
                  ? "Salvar nova versão do checklist"
                  : "Salvar validação"
              }}
            </button>
          </div>
        </footer>
      </div>
    </div>
  </div>
</template>
<script>
import { api } from "../services/api";
const clone = (v) => JSON.parse(JSON.stringify(v));
const path = "/api/selo/validacao-ia";
export default {
  name: "SeloValidacaoIa",
  data: () => ({
    enabled: false,
    loading: true,
    loadId: 0,
    busy: false,
    radioEpoch: 0,
    error: "",
    notice: "",
    rooms: [],
    room: null,
    q3: false,
    rows: [],
    search: "",
    stateFilter: "",
    expanded: {},
    dialog: null,
    current: null,
    targetFile: null,
    pendingFlag: null,
    sourceChoice: "IA",
    form: {},
    dialogError: "",
    editIssue: null,
    historyRows: [],
    actionsList: ["APROVAR", "APROVAR PARCIALMENTE", "REJEITAR"],
    checks: [
      { key: "quatroCursos", label: "Mínimo de 4 cursos distintos" },
      {
        key: "gestoresMunicipais",
        label: "Contrata Mais Brasil — Gestores Municipais",
      },
      { key: "mei", label: "Contrata Mais Brasil — MEI" },
      { key: "inclusao2h", label: "Inclusão socioprodutiva — mínimo 2h" },
      {
        key: "comportamentais4h",
        label: "Competências comportamentais — mínimo 4h",
      },
      { key: "reforma1h", label: "Reforma Tributária — mínimo 1h" },
      { key: "horas30", label: "Mínimo de 30h de capacitação válida" },
      { key: "documentosValidos", label: "Documentação comprobatória válida" },
      { key: "horasComputadas", label: "Horas devidamente computadas" },
    ],
    generalChecks: [
      {
        key: "arquivosConformesRegulamento",
        label: "Os arquivos permitem a conferência documental conforme os padrões do regulamento?",
      },
      {
        key: "equipeConfere",
        label:
          "As evidências condizem com a quantidade e os nomes da inscrição ou declaração?",
      },
      {
        key: "ano2026Confere",
        label:
          "Os certificados das horas válidas de todos os atendentes correspondem a 2026?",
      },
    ],
    declarationChecks: [
      { key: "tempestiva", label: "Enviada no prazo" },
      { key: "assinada", label: "Assinatura válida" },
      {
        key: "modeloAdequado",
        label:
          "Modelo e dados completos: responsável, nomes, quantidade e motivo",
      },
    ],
  }),
  computed: {
    filtered() {
      const s = this.search.toLowerCase();
      return this.rows.filter(
        (r) =>
          (!this.stateFilter || r.estado === this.stateFilter) &&
          (!s || JSON.stringify(r.analise).toLowerCase().includes(s))
      );
    },
    ready() {
      return this.filtered.filter((r) => r.podeSincronizar);
    },
    dialogTitle() {
      return this.dialog === "compare"
        ? "Comparar avaliações"
        : this.dialog === "history"
        ? "Histórico de validação e sincronização"
        : this.q3
        ? "Nova revisão do critério 5"
        : "Discordar da IA";
    },
    certificateFiles() {
      return this.current
        ? this.current.analise.files.filter(
            (f) => f.kind !== "DECLARAÇÃO DE EQUIPE"
          )
        : [];
    },
    calculated() {
      const c = this.form.c5;
      if (c && c.arquivosConformesRegulamento === false)
        return { complete: !!String(c.observacoesCriterio || '').trim(), min: 0, points: 0 };
      if (!c || !c.atendentes.length) return { complete: false };
      const ps = c.atendentes;
      if (
        ps.some(
          (p) =>
            p.horasValidas === null ||
            p.horasValidas === "" ||
            this.checks.some((x) => typeof p.itens[x.key] !== "boolean")
        ) ||
        typeof c.equipeConfere !== "boolean" ||
        typeof c.ano2026Confere !== "boolean"
      )
        return { complete: false };
      const min = Math.min(...ps.map((p) => Number(p.horasValidas)));
      return {
        complete: true,
        min,
        points:
          !c.equipeConfere ||
          !c.ano2026Confere ||
          ps.some((p) => this.checks.some((x) => !p.itens[x.key]))
            ? 0
            : min >= 60
            ? 7
            : min >= 35
            ? 5
            : min >= 30
            ? 3.5
            : 0,
      };
    },
    reviewPeople() {
      return this.form.c5 ? this.form.c5.atendentes.filter(p => (this.form.selectedPeople || []).includes(p.chave)) : [];
    },
    teamScoreReasons() {
      if (!this.calculated.complete) return [];
      const c = this.form.c5, reasons = [];
      if (c.arquivosConformesRegulamento === false)
        return ['Os arquivos estão em desconformidade com o regulamento. ' + (c.observacoesCriterio || '')];
      if (!c.equipeConfere) reasons.push('A quantidade ou os nomes da equipe não conferem.');
      if (!c.ano2026Confere) reasons.push('A conferência geral das horas válidas de 2026 não foi cumprida.');
      c.atendentes.forEach(p => {
        const pending = this.checks.filter(check => !p.itens[check.key]).map(check => check.label);
        if (Number(p.horasValidas) < 30 || pending.length)
          reasons.push(p.nome + ': ' + this.num(p.horasValidas) + 'h válidas.' +
            (Number(p.horasValidas) < 30 ? ' Abaixo do mínimo de 30h.' : '') +
            (pending.length ? ' Requisitos não cumpridos: ' + pending.join('; ') + '.' : ''));
      });
      if (this.calculated.points > 0) {
        const names = c.atendentes.filter(p => Number(p.horasValidas) === this.calculated.min).map(p => p.nome);
        reasons.push('A faixa da equipe é determinada pela menor carga válida: ' + this.num(this.calculated.min) + 'h (' + names.join(', ') + ').');
      }
      return reasons;
    },
  },
  async mounted() {
    try {
      const { data } = await api.get(path + "/filtros");
      this.enabled = data.habilitado;
      this.rooms = (data.municipios || []).slice().sort((a,b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base', numeric: true }));
      if (this.rooms.length) {
        this.room = this.rooms[0].id;
        await this.load();
      }
    } catch (e) {
      this.error = this.message(e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    pendingFiles(row) {
      return row.analise.files.filter(f => !['CONCORDO', 'DISCORDO'].includes(this.fileReview(row, f.id).flag));
    },
    personReviewed(row, key) {
      const r = row.revisao;
      return !!(r && r.checklistRefeito && (!r.c5ConfirmedPeople || r.c5ConfirmedPeople.includes(key)));
    },
    async confirmPending(row) {
      const ids = this.pendingFiles(row).map(f => f.id);
      if (!ids.length) return;
      if (row.estado === 'DECISAO_OFICIAL_ALTERADA') return this.compare(row, null, 'CONCORDO');
      const review = this.baseReview(row);
      review.confirmationFileIds = ids;
      await this.persist(row, review, 'IA');
    },
    humanChecklist(row) {
      return (row.estado !== 'DECISAO_OFICIAL_ALTERADA' && row.revisao && row.revisao.c5) || row.oficial.checklist || null;
    },
    historyLines(value) {
      try {
        const d = typeof value === 'string' ? JSON.parse(value) : value;
        const lines = [];
        const show = (v, label) => {
          if (!v) return;
          const c = v.criterion || v;
          if (c.action || c.points != null) lines.push(label + ': ' + (c.action || 'Decisão registrada') + (c.points != null ? ' · ' + this.num(c.points) + ' pontos' : ''));
          if (c.justification || c.comment) lines.push('Justificativa: ' + (c.justification || c.comment));
          const checklist = v.c5 || v.checklist;
          if (v.c5Scope) {
            const selected = (checklist && checklist.atendentes || []).filter(p => (v.c5Scope.people || []).includes(p.chave));
            if (selected.length) lines.push('Atendentes revisados: ' + selected.map(p => p.nome).join(', '));
            lines.push(v.c5Scope.global ? 'Conferências gerais revisadas.' : 'Demais atendentes e dados gerais preservados.');
          }
          (checklist && checklist.atendentes || []).forEach(p => lines.push(p.nome + ': ' + this.num(p.horasValidas) + 'h confirmadas'));
          (v.files || []).forEach(f => {
            const source = this.current.analise.files.find(a => a.id === f.id);
            if (f.action) lines.push((f.name || (source && source.name) || 'Arquivo de evidência') + ': ' + f.action + (f.justification ? ' — ' + f.justification : ''));
          });
        };
        show(d.anterior || d.antes || d.oficialAntes, 'Antes');
        show(d.revisao || d.depois || d.oficialDepois || d, 'Decisão registrada');
        return lines.length ? lines : ['Evento registrado. Consulte a versão e a data acima.'];
      } catch (_) { return ['Evento registrado. Consulte a versão e a data acima.']; }
    },
    message(e) {
      return (
        (e.response &&
          e.response.data &&
          (e.response.data.mensagem || e.response.data.message)) ||
        e.message ||
        "Não foi possível concluir a operação."
      );
    },
    async load() {
      if (!this.room) return;
      const requestId = ++this.loadId;
      this.loading = true;
      this.error = "";
      this.notice = "";
      this.rows = [];
      try {
        const { data } = await api.get(path + "/criterios", {
          params: { municipioId: this.room, capacitacao: this.q3 },
        });
        if (requestId !== this.loadId) return;
        this.rows = data;
        const first =
          this.rows.find((r) => r.analise.questionId === (this.q3 ? 3 : 2)) ||
          this.rows[0];
        if (first) this.$set(this.expanded, first.id, true);
      } catch (e) {
        if (requestId === this.loadId) this.error = this.message(e);
      } finally {
        if (requestId === this.loadId) this.loading = false;
      }
    },
    async refresh(row) {
      const { data } = await api.get(path + "/criterios/" + row.id);
      const i = this.rows.findIndex((r) => r.id === row.id);
      if (i >= 0) this.$set(this.rows, i, data);
      return data;
    },
    num(n) {
      return n == null
        ? "—"
        : Number(n).toLocaleString("pt-BR", { maximumFractionDigits: 4 });
    },
    date(v) {
      if (!v) return "Data não registrada";
      // Official legacy timestamps are UTC without an offset; new audit fields
      // already carry Z. The evaluator always reads Rio de Janeiro time.
      let stamp = String(v).replace(" ", "T");
      if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(stamp)) stamp += "Z";
      return new Date(stamp).toLocaleString("pt-BR", {
        timeZone: "America/Sao_Paulo",
      });
    },
    shortRoom(v) {
      return (v || "").split(" - Sala")[0];
    },
    boolText(v) {
      return v === true
        ? "Cumprido"
        : v === false
        ? "Não cumprido"
        : "Não informado";
    },
    tone(p, m) {
      return p === 0 ? "red" : p >= m ? "green" : "yellow";
    },
    actionTone(a) {
      return a === "REJEITAR"
        ? "red"
        : a === "APROVAR PARCIALMENTE"
        ? "yellow"
        : "green";
    },
    stateTone(s) {
      return s === "SINCRONIZADO"
        ? "green"
        : s === "NOVA_ANALISE" || s === "DECISAO_OFICIAL_ALTERADA"
        ? "red"
        : "neutral";
    },
    stateLabel(s) {
      return (
        {
          PRONTO: "Pronto para sincronizar",
          REABERTO: "Reaberto · nova sincronização",
          SINCRONIZADO: "Sincronizado",
          NOVA_ANALISE: "Nova análise necessária",
          DECISAO_OFICIAL_ALTERADA: "Avaliação oficial alterada",
          AGUARDANDO_VALIDACAO: "Aguardando validação",
        }[s] || s
      );
    },
    fileReview(r, id) {
      return (
        (r.revisao &&
          r.revisao.files &&
          r.revisao.files.find((f) => f.id === id)) ||
        {}
      );
    },
    officialFile(r, id) {
      return r.oficial.files.find((f) => f.id === id) || {};
    },
    fileName(r, id) {
      return (
        (r.analise.files.find((f) => f.id === id) || {}).name || String(id)
      );
    },
    courses(r, p) {
      return (r.analise.courses || []).filter((c) => c.person === p.nome);
    },
    aiPerson(key) {
      return (
        this.current.analise.c5.atendentes.find((p) => p.chave === key) || {}
      );
    },
    baseReview(row) {
      return clone(
        row.revisao || {
          flag: null,
          points: row.analise.points,
          bandId: row.analise.bandId,
          action: row.analise.decision,
          justification: "",
          c5: row.analise.c5 || null,
          files: row.analise.files.map((f) => ({
            id: f.id,
            flag: null,
            action: f.decision,
            justification: "",
          })),
        }
      );
    },
    async mark(row, id, flag) {
      this.error = "";
      if (row.arquivosAlterados) return;
      if (
        row.avaliacaoOficialExistente &&
        !row.avaliacoesCoincidem &&
        (!row.revisao || row.revisao.officialHash !== row.oficialHash)
      ) {
        this.compare(row, id, flag);
        return;
      }
      if (flag === "DISCORDO") {
        this.edit(row, id, "NOVA");
        return;
      }
      if (row.analise.questionId === 3 && id == null && row.revisao && row.revisao.checklistRefeito)
        return this.confirmPending(row);
      const review = this.baseReview(row);
      if (row.analise.questionId === 3 && id != null) {
        review.confirmationFileIds = [id];
        await this.persist(row, review, 'IA');
        return;
      }
      if (id == null) {
        review.flag = "CONCORDO";
        review.justification = "";
      } else {
        const f = review.files.find((f) => f.id === id);
        f.flag = "CONCORDO";
        f.justification = "";
        if (
          review.flag === "DISCORDO" &&
          !review.files.some((f) => f.flag === "DISCORDO")
        )
          review.flag = null;
      }
      await this.persist(row, review, "IA");
    },
    compare(row, id, flag) {
      if (!row.avaliacaoOficialExistente) return this.mark(row, id, flag);
      this.current = row;
      this.targetFile = id;
      this.pendingFlag = flag;
      this.dialog = "compare";
      this.dialogError = "";
    },
    async choose(choice) {
      const r = this.current,
        id = this.targetFile,
        flag = this.pendingFlag;
      if (
        choice === "NOVA" ||
        (choice === "OFICIAL" && !r.avaliacoesCoincidem) ||
        flag === "DISCORDO"
      ) {
        this.edit(r, id, choice === "IA" ? "NOVA" : choice);
        return;
      }
      const review = this.baseReview(r);
      review.flag = "CONCORDO";
      // The comparison explicitly chose the original IA or the current official decision.
      review.replaceHumanC5 = true;
      this.close();
      await this.persist(r, review, choice);
    },
    edit(row, id, choice) {
      this.current = row;
      this.targetFile = id;
      this.sourceChoice = choice;
      this.dialog = "edit";
      this.dialogError = "";
      const old = this.baseReview(row),
        official = choice === "OFICIAL";
      this.form = {
        justification: "",
        fileAction: official ? this.officialFile(row, id).action || "" : ((old.files.find(f => f.id === id) || {}).action || ""),
        files: old.files.map((f) => ({
          ...f,
          action: official
            ? this.officialFile(row, f.id).action
            : f.action,
        })),
        action: official ? row.oficial.criterion.action : "",
        bandId: official ? row.oficial.criterion.bandId : null,
        points: official ? row.oficial.criterion.points : null,
        ignoredIds: [],
        c5: null,
      };
      if (row.analise.questionId === 3) {
        this.editIssue = null;
        const useOfficial = official || row.estado === 'DECISAO_OFICIAL_ALTERADA';
        const base = useOfficial ? row.oficial.checklist : (row.revisao && row.revisao.c5) || row.oficial.checklist || row.analise.c5;
        const c = clone(base || row.oficial.checklist || row.analise.c5);
        c.declaracoes = c.declaracoes || [];
        c.atendentes = c.atendentes || [];
        this.form.selectedPeople = c.atendentes.filter(p => id == null || (p.arquivoIds || []).includes(id)).map(p => p.chave);
        this.form.reviewGlobal = id == null || c.declaracoes.some(d => d.arquivoId === id);
        this.form.wholeTeam = id == null;
        this.form.scopeConfirmed = false;
        this.form.base = useOfficial && row.oficial.checklist ? 'OFICIAL' : 'VIGENTE';
        this.form.ignoredIds = (c.arquivosDesconsiderados || []).map(f => f.arquivoId);
        this.form.c5 = c;
      }
    },
    reviewWholeTeam() {
      this.form.wholeTeam = true;
      this.form.reviewGlobal = true;
      this.form.selectedPeople = this.form.c5.atendentes.map(p => p.chave);
      this.form.scopeConfirmed = false;
    },
    removePerson(key) {
      this.form.c5.atendentes = this.form.c5.atendentes.filter(p => p.chave !== key);
      this.form.selectedPeople = this.form.selectedPeople.filter(k => k !== key);
    },
    addPerson() {
      this.form.c5.atendentes.push({
        chave: "humano-" + Date.now(),
        nome: "",
        arquivoIds: [],
        itens: Object.fromEntries(this.checks.map((c) => [c.key, null])),
        horasValidas: null,
        semCertificado: false,
        observacoes: "",
      });
      this.form.selectedPeople = this.form.c5.atendentes.map(p => p.chave);
    },
    async saveEdit() {
      this.dialogError = "";
      this.editIssue = null;
      const row = this.current,
        f = this.form;
      if (!f.justification.trim()) {
        this.dialogError = "Informe a justificativa da discordância.";
        return;
      }
      const review = this.baseReview(row);
      review.flag = "DISCORDO";
      review.justification = f.justification;
      review.action = f.action;
      review.bandId = f.bandId;
      review.points = f.points;
      review.files = this.targetFile
        ? review.files
        : f.files.map((x) => ({
            ...x,
            flag:
              x.action ===
              (row.analise.files.find((a) => a.id === x.id) || {}).decision
                ? "CONCORDO"
                : "DISCORDO",
            justification: f.justification,
          }));
      if (this.targetFile) {
        if (!f.fileAction) {
          this.dialogError = "Selecione a ação correta para o arquivo.";
          return;
        }
        const file = review.files.find((x) => x.id === this.targetFile);
        Object.assign(file, {
          flag: "DISCORDO",
          action: f.fileAction,
          justification: f.justification,
        });
      } else if (review.files.some((x) => !x.action)) {
        this.dialogError = "Avalie todos os arquivos deste critério.";
        return;
      }
      if (row.analise.questionId === 3) {
        if (!f.selectedPeople.length && !f.reviewGlobal) {
          this.failEdit('Selecione ao menos um atendente ou habilite a revisão das conferências gerais.', 'c5Selection');
          return;
        }
        if (!this.calculated.complete) {
          this.failEdit('Confira os dados pendentes do checklist da equipe para calcular a faixa.', 'c5Preview');
          return;
        }
        if (f.points === null || Number(f.points) !== this.calculated.points) {
          this.failEdit('No campo “Confirmar nova faixa do critério 5”, selecione ' + this.num(this.calculated.points) + ' ponto(s), conforme checklist.', 'c5Points');
          return;
        }
        if (!f.scopeConfirmed) {
          this.failEdit('Marque a confirmação “Conferi os atendentes selecionados…” no final do formulário antes de salvar.', 'c5Confirmation');
          return;
        }
        const c = clone(f.c5);
        c.quantidadeEquipeAtual = c.atendentes.length;
        c.nomesEquipeAtual = c.atendentes.map((p) => p.nome);
        c.observacoesCriterio = f.justification;
        c.arquivosDesconsiderados = f.ignoredIds.map((id) => ({
          arquivoId: id,
          justificativa: f.justification,
        }));
        review.c5 = c;
        review.checklistRefeito = true;
        review.c5Scope = { people: f.selectedPeople.slice(), global: f.reviewGlobal,
          wholeTeam: f.wholeTeam, confirmed: f.scopeConfirmed, fileId: this.targetFile, base: f.base };
      } else if (row.analise.bands.length && f.bandId === null) {
        this.dialogError = "Selecione a nova faixa do critério.";
        return;
      } else if (!row.analise.bands.length && !f.action) {
        this.dialogError = "Selecione Aprovar ou Rejeitar para o critério.";
        return;
      }
      await this.persist(row, review, this.sourceChoice);
    },
    failEdit(message, field) {
      this.dialogError = message;
      this.editIssue = field;
      if (this.$nextTick) this.$nextTick(() => {
        const element = this.$refs[field];
        if (element) {
          element.scrollIntoView({ block: 'center', behavior: 'smooth' });
          element.focus({ preventScroll: true });
        }
      });
    },
    async persist(row, review, choice) {
      this.busy = true;
      try {
        await api.post(path + "/criterios/" + row.id + "/revisao", {
          expectedVersion: row.versao,
          officialHash: row.oficialHash,
          choice,
          review,
        });
        this.close();
        const fresh = await this.refresh(row);
        this.notice = fresh.podeSincronizar
          ? "Validação salva. Este critério está pronto para sincronizar."
          : "Revisão salva. Falta confirmar: " + this.pendingFiles(fresh).map(f => f.name).join('; ') + ". As revisões anteriores foram preservadas.";
      } catch (e) {
        if (this.dialog) this.dialogError = this.message(e);
        else this.error = this.message(e);
        if (e.response && e.response.status === 409) await this.refresh(row);
      } finally {
        this.busy = false;
      }
    },
    async sync(row, confirmed = false) {
      if (
        !confirmed &&
        !window.confirm(
          "Sincronizar este critério com a análise oficial de Evidências? O histórico anterior será preservado."
        )
      )
        return;
      this.busy = true;
      this.error = "";
      try {
        await api.post(path + "/criterios/" + row.id + "/sincronizar", {
          expectedVersion: row.versao,
        });
        await this.refresh(row);
        this.notice =
          "Critério sincronizado e conferido na análise oficial. Os demais critérios não foram alterados.";
      } catch (e) {
        this.error = this.message(e);
        await this.refresh(row);
      } finally {
        this.busy = false;
      }
    },
    async syncReady() {
      const selected = this.ready.slice();
      if (
        !selected.length ||
        !window.confirm(
          "Sincronizar " +
            selected.length +
            " critérios validados desta seleção? Os demais permanecerão pendentes."
        )
      )
        return;
      const errors = [];
      let done = 0;
      for (const row of selected) {
        this.busy = true;
        try {
          await api.post(path + "/criterios/" + row.id + "/sincronizar", {
            expectedVersion: row.versao,
          });
          done++;
        } catch (e) {
          errors.push(row.analise.code + ": " + this.message(e));
        }
        await this.refresh(row);
      }
      this.busy = false;
      this.notice = done + " critério(s) sincronizado(s).";
      this.error = errors.join(" · ");
    },
    async showHistory(row) {
      this.current = row;
      this.dialog = "history";
      this.dialogError = "";
      try {
        const { data } = await api.get(
          path + "/criterios/" + row.id + "/historico"
        );
        this.historyRows = data;
      } catch (e) {
        this.dialogError = this.message(e);
      }
    },
    historyText(v) {
      try {
        return JSON.stringify(JSON.parse(v), null, 2);
      } catch (_) {
        return v;
      }
    },
    close() {
      this.dialog = null;
      this.dialogError = "";
      this.radioEpoch++;
    },
  },
};
</script>
<style scoped>
.field-error { outline: 2px solid #a13340; outline-offset: 3px; border-radius: 5px; }
.field-error-text { color: #a13340; font-weight: 600; }
.loading-state { display:flex; align-items:center; gap:12px; min-height:90px; }
.loading-spinner { width:24px; height:24px; border:3px solid #d7e5f2; border-top-color:#17598c; border-radius:50%; animation:selo-loading .8s linear infinite; }
@keyframes selo-loading { to { transform:rotate(360deg); } }
.flags label, .flags input:not(:disabled), select:not(:disabled) { cursor:pointer !important; }
.flags input:disabled, button:disabled { cursor:default !important; }
.selo-ia-validacao .message.info {
  background: #edf5ff !important;
  color: #315d87 !important;
}
.selo-ia-validacao .message.warning {
  background: #fff2d3 !important;
  color: #76540e !important;
}
.selo-ia-validacao .message.error {
  background: #ffe9eb !important;
  color: #a13340 !important;
}
.selo-ia-validacao .pill.green {
  background: #e2f2e8 !important;
  color: #256a43 !important;
}
.selo-ia-validacao .pill.yellow {
  background: #fff1c6 !important;
  color: #805b0c !important;
}
.selo-ia-validacao .pill.red {
  background: #fbe3e7 !important;
  color: #a32e3b !important;
}
.selo-ia-validacao {
  color: #253b54;
  font: 14px/1.55 "Segoe UI", Arial, sans-serif;
  padding: 20px;
  min-width: 0;
}
.selo-ia-validacao * {
  box-sizing: border-box;
  min-width: 0;
}
header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
}
h1 {
  font-size: 25px;
  margin: 4px 0;
}
h2 {
  font-size: 20px;
  margin: 0;
}
h3 {
  font-size: 15px;
  margin: 13px 0;
}
p {
  margin: 7px 0;
  white-space: pre-line;
  overflow-wrap: anywhere;
}
small {
  display: block;
  font-size: 11px;
  color: #698097;
}
button {
  font: inherit;
  cursor: pointer;
  border: 1px solid #c4d4e5;
  padding: 8px 12px;
  border-radius: 5px;
  color: #1c5a96;
  background: white;
  max-width: 100%;
}
button.primary {
  background: #1c5a96;
  color: white;
}
button:disabled {
  opacity: 0.5;
  cursor: default;
}
.actions,
.choice-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.message {
  padding: 12px 15px;
  border-radius: 6px;
  margin: 12px 0;
  overflow-wrap: anywhere;
}
.info {
  background: #edf5ff;
  color: #315d87;
}
.warning {
  background: #fff2d3;
  color: #826019;
}
.error {
  background: #ffe9eb;
  color: #a13340;
}
.tabs {
  display: flex;
  gap: 8px;
  border-bottom: 1px solid #d2dce9;
  margin: 18px 0;
}
.tabs button {
  border: 0;
  border-radius: 0;
}
.tabs button.active {
  border-bottom: 3px solid #1c5a96;
  font-weight: bold;
}
.filters {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 0.7fr);
  gap: 14px;
  padding: 15px;
  background: #f5f8fc;
  border: 1px solid #d8e2ed;
  border-radius: 7px;
}
label.field,
.filters label {
  display: block;
  font-size: 12px;
  font-weight: 600;
}
select,
input:not([type="radio"]):not([type="checkbox"]),
textarea {
  display: block;
  width: 100%;
  max-width: 100%;
  font: inherit;
  color: #263f59;
  border: 1px solid #bdcede;
  border-radius: 5px;
  background: white;
  padding: 8px;
  margin: 5px 0 10px;
}
select[multiple] {
  min-height: 80px;
}
textarea {
  resize: vertical;
}
.metrics {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin: 16px 0;
}
.metrics span {
  padding: 9px 13px;
  border: 1px solid #dce4ee;
  border-radius: 6px;
  font-size: 12px;
}
.metrics strong {
  font-size: 18px;
  margin-right: 5px;
}
.grid-head,
.mainrow {
  display: grid;
  grid-template-columns: 24px minmax(0, 0.8fr) minmax(0, 1.5fr) minmax(0, 1fr) 75px minmax(
      155px,
      1fr
    );
  gap: 12px;
  padding: 15px;
  align-items: center;
}
.grid-head {
  background: #eaf1f8;
  font-size: 10px;
  text-transform: uppercase;
  font-weight: 700;
}
.criterion {
  border: 1px solid #d9e2ed;
  border-top: 0;
  background: white;
}
.mainrow {
  font-size: 12px;
}
.mainrow .toggle {
  padding: 0;
  border: 0;
  font-size: 20px;
  background: transparent;
}
.question {
  overflow-wrap: anywhere;
}
.question b {
  color: #175b9f;
}
.points {
  font-size: 19px;
  font-weight: 700;
  white-space: nowrap;
}
.points small {
  display: inline;
  font-weight: 400;
  margin-left: 5px;
}
.flags {
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 11px;
}
.flags label {
  display: flex;
  gap: 6px;
  cursor: pointer;
  align-items: flex-start;
}
.flags input {
  flex: 0 0 14px;
  margin: 3px 0 0;
  accent-color: #1e64a4;
}
.pill {
  display: inline-block;
  padding: 4px 7px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  overflow-wrap: anywhere;
}
.green {
  color: #256a43;
  background: #e2f2e8;
}
.yellow {
  color: #805b0c;
  background: #fff1c6;
}
.red {
  color: #a32e3b;
  background: #fbe3e7;
}
.neutral {
  color: #4f6987;
  background: #edf1f6;
}
.criterion-meta {
  display: flex;
  gap: 9px;
  align-items: center;
  flex-wrap: wrap;
  padding: 0 15px 14px 50px;
  font-size: 11px;
}
.criterion-meta > span:first-child {
  flex: 1;
  min-width: 130px;
}
.criterion-meta button {
  font-size: 11px;
  padding: 5px 8px;
}
.criterion > .message {
  margin: 0 15px 13px 50px;
  font-size: 12px;
}
.detail {
  padding: 0 15px 18px 50px;
}
.comparison,
.checklist {
  padding: 15px;
  border: 1px solid #cbddef;
  border-radius: 6px;
  background: #f7fbff;
  margin: 12px 0;
}
.comparison-grid,
.team {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
}
.comparison-file {
  padding: 12px 0;
  border-bottom: 1px solid #dbe3ee;
  font-size: 12px;
}
.comparison-file > span {
  margin-left: 12px;
}
.choice-buttons {
  margin-top: 18px;
}
.human {
  padding: 12px;
  margin: 10px 0;
  background: #fff8e8;
  border-left: 4px solid #d1a04b;
  font-size: 12px;
}
.evidence {
  border: 1px solid #d1deed;
  border-radius: 6px;
  margin: 12px 0;
}
.filehead {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(155px, 0.45fr);
  gap: 14px;
  padding: 12px;
  background: #f1f6fc;
  align-items: center;
}
.filehead a {
  font-size: 12px;
  font-weight: 600;
  overflow-wrap: anywhere;
}
.copygrid {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  gap: 20px;
  padding: 15px;
  font-size: 12px;
}
.copygrid b {
  font-size: 10px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #637e99;
}
.official-file {
  padding: 12px;
  border-top: 1px dashed #cedce9;
  background: #f9fbfd;
  font-size: 12px;
}
.checklist summary,
.person summary {
  cursor: pointer;
  color: #1b5c97;
  font-weight: 600;
}
.person {
  margin: 12px 0;
  padding: 12px;
  background: white;
  border: 1px solid #d6e1ee;
  border-radius: 6px;
}
.person p {
  font-size: 12px;
}
.checkline {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(100px, 0.6fr);
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid #e1e8f1;
  font-size: 12px;
  align-items: center;
}
.checkline select {
  margin: 0;
}
.text-green {
  color: #24734b;
}
.text-red {
  color: #a3303a;
}
.course {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 10px 0;
  border-bottom: 1px solid #dde7f2;
  font-size: 12px;
  overflow-wrap: anywhere;
}
.overlay {
  position: fixed;
  inset: 0;
  background: #18334cb8;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2500;
}
.dialog {
  width: min(100%, 1050px);
  max-height: calc(100dvh - 40px);
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 8px;
  box-shadow: 0 20px 70px #17314c66;
}
.dialog > header {
  padding: 15px 20px;
  background: #22567f;
  color: white;
  border-radius: 8px 8px 0 0;
}
.dialog > header button {
  padding: 0 7px;
  border: 0;
  background: transparent;
  color: white;
  font-size: 22px;
}
.dialog-body {
  overflow: auto;
  padding: 20px;
}
.dialog .field {
  margin: 14px 0;
}
.dialog footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  padding: 14px 20px;
  border-top: 1px solid #d8e2ed;
}
.dialog footer > div {
  display: flex;
  gap: 8px;
}
.dialog pre {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: 11px;
}
.checkbox {
  display: flex !important;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
}
.checkbox input {
  flex: 0 0 14px;
  margin-top: 4px;
}
@media (max-width: 1100px) {
  .selo-ia-validacao {
    padding: 12px;
  }
  .grid-head {
    display: none;
  }
  .mainrow {
    grid-template-columns: 22px minmax(0, 1fr) minmax(155px, 0.6fr);
    gap: 9px;
  }
  .mainrow > button {
    grid-row: 1/4;
  }
  .mainrow > div:nth-child(2) {
    grid-column: 2;
  }
  .mainrow > .question {
    grid-column: 2;
    grid-row: 2;
  }
  .mainrow > div:nth-child(4) {
    grid-column: 2;
    grid-row: 3;
  }
  .mainrow > .points {
    grid-column: 3;
    grid-row: 3;
  }
  .mainrow > .flags {
    grid-column: 3;
    grid-row: 1/3;
  }
  .filters {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
  .filters > label:first-child {
    grid-column: 1/-1;
  }
  .detail {
    padding-left: 15px;
  }
  .filehead {
    grid-template-columns: minmax(0, 1fr) minmax(155px, 0.5fr);
  }
  .filehead > .pill {
    grid-column: 1;
    grid-row: 2;
  }
  .filehead > .flags {
    grid-column: 2;
    grid-row: 1/3;
  }
}
@media (max-width: 650px) {
  header {
    align-items: flex-start;
    flex-direction: column;
  }
  .filters,
  .comparison-grid,
  .team,
  .copygrid {
    grid-template-columns: minmax(0, 1fr);
  }
  .filters > label:first-child {
    grid-column: 1;
  }
  .mainrow {
    grid-template-columns: 20px minmax(0, 1fr);
  }
  .mainrow > .points {
    grid-column: 2;
    grid-row: 4;
  }
  .mainrow > .flags {
    grid-column: 2;
    grid-row: 5;
    flex-direction: row;
    gap: 12px;
    flex-wrap: wrap;
  }
  .criterion-meta {
    padding-left: 15px;
  }
  .criterion > .message {
    margin-left: 15px;
  }
  .filehead {
    grid-template-columns: minmax(0, 1fr);
  }
  .filehead > .flags {
    grid-column: 1;
    grid-row: 3;
    flex-direction: row;
    gap: 12px;
    flex-wrap: wrap;
  }
  .copygrid {
    gap: 14px;
  }
  .overlay {
    padding: 5px;
  }
  .dialog {
    max-height: calc(100dvh - 10px);
  }
  .dialog-body {
    padding: 12px;
  }
  .dialog > header,
  .dialog footer {
    padding: 12px;
  }
  .checkline {
    grid-template-columns: minmax(0, 1fr);
  }
  .comparison-file > span {
    display: block;
    margin-left: 0;
  }
}
</style>
